<template>
  <div>
    <GlobalNavigation />

    <main class="py-8 mb-16">

      <BaseContainer>
        <div class="text-center">
          <BaseButton color="muted" to="/">
            <DropdownIcon class="rotate-90 h-4 w-4 mr-2 fill-text" />
            {{ $t('common.back_to_overview') }}
          </BaseButton>
    
          <h1 class="mb-6 mt-6 h1">{{ $t('contact.title') }}</h1>
        </div>
      </BaseContainer>

      <BaseContainer>
        <div class="max-w-[450px] mx-auto prose prose-lg">

          <p v-html="$t('contact.description', { url: `<span class='font-bold'>${location.origin}</span>` })"></p>

          <p>
            <span class="font-bold">{{ $t('contact.organizer') }}:</span> {{ customer.name }}
          </p>
          <p>
            <span class="font-bold">{{ $t('contact.contact_person') }}:</span> {{ customer.contact }}
          </p>
          <p>
            <span class="font-bold">{{ $t('contact.phone') }}:</span> {{ customer.telephone }}
          </p>
          <p>
            <span class="font-bold">{{ $t('contact.email') }}:</span> {{ customer.email }}
          </p>

          <p>{{ $t('contact.contact_form_info') }}</p>

          <h2 class="h2">{{ $t('contact.contact_form') }}</h2>
        </div>
      </BaseContainer>
      <Form @submit="handleSubmit" @invalid-submit="onInvalidSubmit">
        <BaseContainer>
          <div class="max-w-[450px] mx-auto prose prose-lg">
            <div class="mb-4">
              <InputText
                id="name"
                name="name"
                type="text"
                :rules="rules.name"
                v-model="form.name"
                :placeholder="$t('contact.name_placeholder')"
                class="mt-1 block w-full border-gray-300 rounded-md"
                required
              />
            </div>

            <div class="mb-4">
              <InputText
                id="phone"
                name="phone"
                type="tel"
                :rules="rules.phone"
                v-model="form.phone"
                :placeholder="$t('contact.phone_placeholder')"
                class="mt-1 block w-full border-gray-300 rounded-md"
                required
              />
            </div>

            <div class="mb-4">
              <InputText
                id="email"
                name="email"
                type="email"
                :rules="rules.email"
                v-model="form.email"
                :placeholder="$t('contact.email_placeholder')"
                class="mt-1 block w-full border-gray-300 rounded-md"
                required
              />
            </div>

            <div class="mb-4">
              <InputText
                id="orderno"
                name="orderno"
                type="text"
                :rules="rules.orderno"
                v-model="form.orderno"
                :placeholder="$t('contact.order_number_placeholder')"
                class="mt-1 block w-full border-gray-300 rounded-md"
                required
              />
            </div>

            <div class="mb-4">
              <InputText
                id="body"
                name="body"
                type="text"
                :rules="rules.body"
                v-model="form.body"
                :placeholder="$t('contact.body_placeholder')"
                class="mt-1 block w-full border-gray-300 rounded-md"
                required
              ></InputText>
            </div>

            <BaseButton 
              key="static"
              class="w-full relative group"
              color="primary" 
              size="lg"
              type="submit"
            >
              <span class="w-full flex items-center justify-center">
                {{ $t('contact.submit') }}
                <ArrowRightIcon
                  class="w-4 h-4 ml-2 fill-current group-hover:animate-pulse-right"
                />
              </span>
            </BaseButton>

            <div v-if="errorMessage" class="text-sm block pt-2 text-error">
              {{ errorMessage }}
            </div>

            <div v-if="successMessage" class="text-sm block pt-2 text-success">
              {{ successMessage }}
            </div>
          </div>
        </BaseContainer>
      </Form>
    </main>
  </div>
</template>

<script setup lang="ts">
import { useHead } from '@unhead/vue';
import { Form, type InvalidSubmissionHandler, type SubmissionHandler } from 'vee-validate';
import { computed, onMounted, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';

const location = window.location;

import { ref } from 'vue';

import { submitContactForm } from '@/api/contact';
import GlobalNavigation from '@/components/globals/GlobalNavigation.vue';
import ArrowRightIcon from '@/components/icons/ArrowRightIcon.vue';
import DropdownIcon from '@/components/icons/DropdownIcon.vue';
import BaseButton from '@/components/ui/BaseButton.vue';
import BaseContainer from '@/components/ui/BaseContainer.vue';
import InputText from '@/components/ui/inputs/InputText.vue';
import { useGeneralStore } from '@/stores/general';

const generalStore = useGeneralStore();
const customer = computed(() => generalStore.customer());

const { t } = useI18n();

useHead({ // TODO implement proper SEO content
  title: t('contact.head_title'),
  meta: [
    {
      name: 'description',
      content: 'Find the latest events and activities happening near you.',
    },
    {
      property: 'og:title',
      content: t('contact.head_title'),
    },
    {
      property: 'og:description',
      content: 'Find the latest events and activities happening near you.',
    },
  ],
});

const form = reactive({
  name: '',
  phone: '',
  email: '',
  orderno: '',
  body: '',
});

const rules = { // TODO test that these work as intended. fix texts (use locales)
  name: yup
    .string()
    .required(t('validation.required')),
  phone: yup
    .string()
    .matches(/^[\d +()-]{7,}$/, 'Phone number is invalid')
    .required(t('validation.required')),
  email: yup
    .string()
    .email(t('validation.email'))
    .required(t('validation.required')),
  orderno: yup
    .number()
    .typeError(t('validation.order_number'))
    .required(t('validation.required')),
  body: yup
    .string()
    .required(t('validation.required')),
};

const errorMessage = ref<string | null>(null);
const successMessage = ref<string | null>(null);

const handleSubmit: SubmissionHandler = async (values) => {
  const formValues = values as {
    name: string;
    phone: string;
    email: string;
    orderno: string;
    body: string;
  };

  console.log(formValues);

  try {
    const success = await submitContactForm(formValues);
    if (success) {
      successMessage.value = t('contact.success_message');
      errorMessage.value = null;
      console.log('Your message has been sent successfully!');
    } else {
      successMessage.value = null;
      errorMessage.value = t('contact.error_message');
      console.error('There was an error submitting your message. Please try again later.');
    }
  } catch {
    successMessage.value = null;
    errorMessage.value = t('contact.error_message');
    console.error('There was an error submitting your message. Please try again later.');
  }
};

const onInvalidSubmit: InvalidSubmissionHandler = submission => {
  const firstErrorFieldName = Object.keys(submission.errors)[0];
  const el = document.querySelector(`[name="${firstErrorFieldName}"]`);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth' });
  }
};

// Fetch general data on mount
onMounted(() => {
  generalStore.fetchGeneral();
});
</script>
